export type MailContextSource = 'fullscreen';
export type MailContextPriority = 'mid' | 'highest';

export const MAX_MAIL_CONTEXT_ATTACHMENTS = 10;

export interface MailMessageAttachment {
  id: string;
  workspaceId: string;
  accountId: string;
  folderId: string | null;
  subject: string;
  sender: string;
  fromAddress?: string | null;
  snippet?: string | null;
  receivedAt?: string | null;
  sentAt?: string | null;
  dateLabel?: string | null;
  source: MailContextSource;
  priority: MailContextPriority;
}

export interface MailSelectionContextEventDetail {
  action: 'update' | 'clear';
  workspaceId: string;
  messages: MailMessageAttachment[];
}

export interface MailSelectionContextRemoveEventDetail {
  workspaceId: string;
  messageId: string;
}

export const MAIL_SELECTION_CONTEXT_EVENT =
  'streamlain:fullscreen-mail-context';

export const MAIL_SELECTION_CONTEXT_REMOVE_EVENT =
  'streamlain:fullscreen-mail-context-remove';

const MAIL_SELECTION_CONTEXT_STATE_KEY = '__streamlainMailSelectionContext';

function setCurrentMailSelectionContext(
  detail: MailSelectionContextEventDetail
) {
  (globalThis as Record<string, unknown>)[MAIL_SELECTION_CONTEXT_STATE_KEY] =
    detail;
}

export function getCurrentMailSelectionContext(): MailSelectionContextEventDetail | null {
  const detail = (globalThis as Record<string, unknown>)[
    MAIL_SELECTION_CONTEXT_STATE_KEY
  ];
  return detail &&
    typeof detail === 'object' &&
    'action' in detail &&
    'workspaceId' in detail &&
    'messages' in detail
    ? (detail as MailSelectionContextEventDetail)
    : null;
}

export function dispatchMailSelectionContext(
  detail: MailSelectionContextEventDetail
) {
  setCurrentMailSelectionContext(detail);
  window.dispatchEvent(
    new CustomEvent<MailSelectionContextEventDetail>(
      MAIL_SELECTION_CONTEXT_EVENT,
      { detail }
    )
  );
}

export function dispatchMailSelectionContextRemove(
  detail: MailSelectionContextRemoveEventDetail
) {
  window.dispatchEvent(
    new CustomEvent<MailSelectionContextRemoveEventDetail>(
      MAIL_SELECTION_CONTEXT_REMOVE_EVENT,
      { detail }
    )
  );
}

export function isMailSelectionContextEvent(
  event: Event
): event is CustomEvent<MailSelectionContextEventDetail> {
  return event.type === MAIL_SELECTION_CONTEXT_EVENT && 'detail' in event;
}

export function isMailSelectionContextRemoveEvent(
  event: Event
): event is CustomEvent<MailSelectionContextRemoveEventDetail> {
  return (
    event.type === MAIL_SELECTION_CONTEXT_REMOVE_EVENT && 'detail' in event
  );
}
